import { useState } from "react";
import { Play } from "lucide-react";

type Props = {
  videoId: string;
  title: string;
  label?: string;
  points?: string[];
};

/**
 * XAU Pro Academy branded player for vertical YouTube Shorts.
 * Facade first (thumbnail + gold play button), loads the YouTube iframe only on click.
 */
export function BrandedShortPlayer({ videoId, title, label, points }: Props) {
  const [playing, setPlaying] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const thumbs = ["maxresdefault", "sddefault", "hqdefault"];
  const thumb = `https://i.ytimg.com/vi/${videoId}/${thumbs[thumbIdx]}.jpg`;

  return (
    <figure className="not-prose my-10 overflow-hidden rounded-2xl border border-gold/30 bg-card/60 shadow-lg backdrop-blur">
      <div className="grid gap-6 p-4 sm:grid-cols-[minmax(0,260px)_1fr] sm:items-center sm:p-6">
        <div className="relative mx-auto aspect-[9/16] w-full max-w-[260px] overflow-hidden rounded-xl border border-border/60 bg-background">
          {playing ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&vq=hd1080&cc_load_policy=0&iv_load_policy=3`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label={`Play video: ${title}`}
              className="group absolute inset-0 h-full w-full"
            >
              <img
                src={thumb}
                alt={title}
                loading="lazy"
                decoding="async"
                onLoad={(e) => {
                  // YouTube returns a 120x90 placeholder when a size is missing
                  if (e.currentTarget.naturalWidth <= 120 && thumbIdx < thumbs.length - 1) setThumbIdx(thumbIdx + 1);
                }}
                onError={() => thumbIdx < thumbs.length - 1 && setThumbIdx(thumbIdx + 1)}
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
              <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/60 bg-background/70 text-gold shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 h-7 w-7 fill-current" />
              </span>
              <span className="absolute bottom-3 left-3 right-3 text-left font-mono text-[10px] uppercase tracking-widest text-gold">
                XAU Pro Academy Desk
              </span>
            </button>
          )}
        </div>
        <figcaption className="space-y-3">
          {label && (
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{label}</p>
          )}
          <p className="font-display text-xl font-semibold leading-snug text-foreground">{title}</p>
          {points && points.length > 0 && (
            <ol className="space-y-2 text-sm leading-relaxed text-foreground/80">
              {points.map((p, i) => (
                <li key={p} className="flex gap-3">
                  <span className="font-mono text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <span>{p}</span>
                </li>
              ))}
            </ol>
          )}
        </figcaption>
      </div>
    </figure>
  );
}
